import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, MonoTypeOperatorFunction, Observable, Subject, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

export interface SplitMergeConflictEvent {
  alertId: string;
  attemptedFields: string[];
}

export interface SplitMergeDisableState {
  disabled: boolean;
  reason: string;
}

export interface SplitMergeDisableInputs {
  isInProgress: boolean;
  isOwner: boolean;
  isValidStatus: boolean;
  hasApprovedFailures: boolean;
}

// i18n keys for the split/merge tooltip copy. The display text lives in
// src/app/core/_config/i18n/*.ts under ALERT_V2.DETAIL.* and is resolved
// through TranslateService so every tooltip follows the standard translate
// flow. Keep these keys (not raw English) as the single source of truth.
const SPLIT_MERGE_INVALID_STATUS_KEY = 'ALERT_V2.DETAIL.SPLIT_MERGE_STATUS';
const SPLIT_MERGE_NO_APPROVED_KEY = 'ALERT_V2.DETAIL.SPLIT_MERGE_APPROVED';
const SPLIT_MERGE_NOT_OWNER_KEY = 'ALERT_V2.DETAIL.SPLIT_MERGE_OWNER';
const SPLIT_MERGE_ENABLED_KEY = 'ALERT_V2.DETAIL.SPLIT_MERGE_ACTION';
const SPLIT_MERGE_IN_PROGRESS_KEY = 'ALERT_V2.DETAIL.SPLIT_MERGE_IN_PROGRESS';
const SPLIT_MERGE_DISMISS_KEY = 'ALERT_V2.DETAIL.SPLIT_MERGE_DISMISS';

// English fallbacks used only if the translation isn't available yet (e.g. the
// key is missing or the root service hasn't been constructed) so a tooltip
// never renders a raw i18n key.
const SPLIT_MERGE_FALLBACKS: Record<string, string> = {
  [SPLIT_MERGE_INVALID_STATUS_KEY]:
    'Only alerts with the status "In Process," "On Watch," "No Action Planned," or "Pending Defect Raised" can be separated into a new alert or merged with an existing one.',
  [SPLIT_MERGE_NO_APPROVED_KEY]:
    'Only approved or unactioned failure mode(s) can be separated to a new alert or merged with an existing alert.',
  [SPLIT_MERGE_NOT_OWNER_KEY]: 'Only an owner of the alert can separate out failure mode(s).',
  [SPLIT_MERGE_ENABLED_KEY]: 'Move failure mode(s) to a new alert or merge with an existing alert.',
  [SPLIT_MERGE_IN_PROGRESS_KEY]:
    'A split or merge is in progress for this alert. Please wait for it to complete.',
  [SPLIT_MERGE_DISMISS_KEY]: 'Dismiss',
};

// Module-scoped TranslateService reference, captured when the root
// SplitMergeConflictService is constructed. Lets the exported pure helper
// `computeSplitMergeDisableState` and the static tooltip getter resolve i18n
// keys without changing their signatures (so call sites stay untouched).
let translateRef: TranslateService | undefined;

function translateSplitMerge(key: string): string {
  const translated = translateRef?.instant(key);
  // ngx-translate echoes the key back when no translation is found.
  if (translated && translated !== key) return translated;
  return SPLIT_MERGE_FALLBACKS[key] ?? key;
}

/**
 * Single source of truth for the split/merge disable decision + tooltip copy.
 * `getToolTipTextForSplitMerge()` is now `computeSplitMergeDisableState(...).reason`,
 * and `isSplitMergeDisabled()` is `.disabled`. Keeping the two answers in lock-step
 * is the whole point — earlier each component recomputed both branches and they
 * could drift.
 */
export function computeSplitMergeDisableState(opts: SplitMergeDisableInputs): SplitMergeDisableState {
  if (opts.isInProgress) {
    return { disabled: true, reason: translateSplitMerge(SPLIT_MERGE_IN_PROGRESS_KEY) };
  }
  if (!opts.isOwner) {
    return { disabled: true, reason: translateSplitMerge(SPLIT_MERGE_NOT_OWNER_KEY) };
  }
  if (!opts.isValidStatus) {
    return { disabled: true, reason: translateSplitMerge(SPLIT_MERGE_INVALID_STATUS_KEY) };
  }
  if (!opts.hasApprovedFailures) {
    return { disabled: true, reason: translateSplitMerge(SPLIT_MERGE_NO_APPROVED_KEY) };
  }
  return { disabled: false, reason: translateSplitMerge(SPLIT_MERGE_ENABLED_KEY) };
}

const SNACKBAR_DURATION_MS = 6000;

interface FailureSnapshot {
  approved: any;
  reason: any;
  recommendations: any;
  newFailure: any;
}

@Injectable({
  providedIn: 'root'
})
export class SplitMergeConflictService {
  // Single source of truth for the user-facing "in progress" copy, resolved
  // through TranslateService. Templates and TS reference this; never inline the
  // string at a call site.
  static get IN_PROGRESS_TOOLTIP(): string {
    return translateSplitMerge(SPLIT_MERGE_IN_PROGRESS_KEY);
  }

  private readonly _conflictSubject = new Subject<SplitMergeConflictEvent>();
  readonly conflict$: Observable<SplitMergeConflictEvent> = this._conflictSubject.asObservable();
  private readonly _lastSnackBarAt = new Map<string, number>();
  private readonly _armedRollbacks = new Map<string, () => void>();

  constructor(
    private readonly _snackBar: MatSnackBar,
    private readonly _translate: TranslateService,
  ) {
    // Expose the injected TranslateService to the module-level helpers so the
    // exported pure function and the static tooltip getter can localize.
    translateRef = this._translate;
  }

  isConflict(error: HttpErrorResponse): boolean {
    if (error?.status !== 409) return false;
    const body = error.error;
    return body?.code === 'SPLIT_MERGE_IN_PROGRESS' || body?.error?.code === 'SPLIT_MERGE_IN_PROGRESS';
  }

  /**
   * RxJS operator for updateAlert-style PUTs. A SPLIT_MERGE_IN_PROGRESS 409 is
   * not a failure as far as callers are concerned — the server has told us the
   * alert is locked. So we flip `isSplitMergeInProgress` on the caller-supplied
   * alert reference, run the side-effects (snackbar + armed rollback + conflict
   * event), and complete silently (EMPTY). The downstream `subscribe(next)`
   * never fires, so optimistic UI writes stay reverted and call sites without
   * an error handler don't print to the console. Non-conflict errors still
   * rethrow.
   *
   * `getAlert` is the live alert reference whose flag should be set — typically
   * the payload passed to updateAlert, or a component's local alert object.
   * Both callbacks are lazy so we capture state at the moment of failure,
   * after any pre-mutations the caller performed.
   */
  notifyOnConflict<T>(
    getAlert: () => { _id?: string; isSplitMergeInProgress?: boolean } | null | undefined,
    getAttemptedFields: () => string[],
  ): MonoTypeOperatorFunction<T> {
    return catchError((error: HttpErrorResponse) => {
      if (this.isConflict(error)) {
        const alert = getAlert();
        if (alert) alert.isSplitMergeInProgress = true;
        this.notifyUpdateConflict(alert?._id, getAttemptedFields());
        return EMPTY;
      }
      return throwError(() => error);
    });
  }

  /**
   * Snapshots {approved, reason, recommendations, newFailure} for every failure
   * on `alert` matching `predicate`, and arms a rollback that restores those
   * values if the next updateAlert is rejected with a SPLIT_MERGE_IN_PROGRESS
   * 409. Disarm on success with `disarmConflictRollback(alert._id)`.
   *
   * `newFailure` is part of the optimistic write — reviewing a failure clears
   * its "new" red dot — so it must be reverted too, otherwise the dot stays
   * gone after a rejected approve/relearn.
   */
  armFailureRollback(alert: any, predicate: (f: any) => boolean): void {
    if (!alert?._id) return;
    const snapshots = new Map<string, FailureSnapshot>();
    for (const f of alert.failures ?? []) {
      if (!predicate(f)) continue;
      const key = f?.mode ?? f?._id;
      if (key == null) continue;
      snapshots.set(String(key), {
        approved: f.approved,
        reason: f.reason,
        recommendations: Array.isArray(f.recommendations)
          ? [...f.recommendations]
          : f.recommendations,
        newFailure: f.newFailure,
      });
    }
    if (!snapshots.size) return;
    this.armConflictRollback(alert._id, () => {
      for (const f of alert?.failures ?? []) {
        const key = f?.mode ?? f?._id;
        const snap = key != null ? snapshots.get(String(key)) : undefined;
        if (!snap) continue;
        f.approved = snap.approved;
        f.reason = snap.reason;
        f.recommendations = snap.recommendations;
        f.newFailure = snap.newFailure;
      }
    });
  }

  // Low-level: arm an arbitrary rollback closure. Prefer `armFailureRollback`
  // for the {approved, reason, recommendations} case; reach for this only when
  // mutating fields outside the failure list.
  armConflictRollback(alertId: string | undefined, rollback: () => void): void {
    if (!alertId) return;
    this._armedRollbacks.set(alertId, rollback);
  }

  disarmConflictRollback(alertId: string | undefined): void {
    if (!alertId) return;
    this._armedRollbacks.delete(alertId);
  }

  /**
   * Call when an updateAlert PUT was rejected with SPLIT_MERGE_IN_PROGRESS 409.
   * Shows the debounced snackbar, fires the armed rollback (so optimistic UI
   * writes snap back to server truth), and emits the conflict event.
   */
  notifyUpdateConflict(alertId?: string, attemptedFields: string[] = []): void {
    const key = alertId ?? '__no_id__';
    const now = Date.now();
    const last = this._lastSnackBarAt.get(key) ?? 0;
    if (now - last >= SNACKBAR_DURATION_MS) {
      this._snackBar.open(
        translateSplitMerge(SPLIT_MERGE_IN_PROGRESS_KEY),
        translateSplitMerge(SPLIT_MERGE_DISMISS_KEY),
        { duration: SNACKBAR_DURATION_MS },
      );
      this._lastSnackBarAt.set(key, now);
    }
    if (alertId) {
      const rollback = this._armedRollbacks.get(alertId);
      this._armedRollbacks.delete(alertId);
      if (rollback) {
        try { rollback(); } catch { /* defensive: don't block the event */ }
      }
      this._conflictSubject.next({ alertId, attemptedFields });
    }
  }

  /**
   * Call when the split/merge API itself returns 202 (queued) or 409 (already
   * in progress). Just emits the event so listeners flip the in-progress flag
   * and start polling — no snackbar (the dialog already shows the status),
   * no armed rollback (the caller didn't pre-mutate anything).
   */
  notifyInProgress(alertId?: string): void {
    if (!alertId) return;
    this._conflictSubject.next({ alertId, attemptedFields: [] });
  }
}
